import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, tap } from 'rxjs';
import {
  DashboardDataRequest,
  DashboardFilterOption,
  DashboardFilterOptionsResponse,
  DashboardFilterState,
  DashboardMetricDefinition,
  DashboardPortfolioFilterOption,
  DashboardSortDirection,
  DashboardScopeOption,
  DashboardScopeResolution,
  DashboardSelectionContext,
  DashboardSummaryPageRequest,
  DashboardSummary,
  DashboardWidgetDrilldownResponse,
  DashboardWidgetDrilldownRow,
  DashboardWidgetSelection,
  DashboardWidgetSaveRequest,
  DashboardWidgetResult
} from 'src/app/core-module/models/Dashboards/dashboard.models';
import { DashboardService } from 'src/app/core-module/services/dashboard.service';
import { dashboardDateRange, DEFAULT_DASHBOARD_DATE_RANGE } from './dashboard-date-range';
import { environment } from 'src/environments/environment';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { CustomerContextService } from 'src/app/core-module/services/customer-context.service';
import {
  BLUEBIRD_DEMO_DASHBOARD_ID,
  bluebirdDemoDashboard,
  bluebirdDemoFilters,
  buildBluebirdDemoDashboardData,
  buildBluebirdDemoDrilldown
} from 'src/app/core-module/services/bluebird-dashboard-demo-data';

type DashboardWidgetTextUpdate = {
  title: string;
  subtitle: string;
  footerText: string;
  icon: string;
};

type BluebirdDashboardSection = {
  key: string;
  title: string;
  metricKeys: string[];
};

const BLUEBIRD_DASHBOARD_NAME = 'Bluebird Care - Local Dashboard';
const BLUEBIRD_EXECUTIVE_DASHBOARD_NAME = 'Bluebird Care - Executive Dashboard';
const BLUEBIRD_DASHBOARD_SECTIONS: BluebirdDashboardSection[] = [
  { key: 'quality-at-a-glance', title: 'Quality at a glance', metricKeys: ['bbc-mandatory-audit-progress', 'bbc-overall-quality-comparison', 'bbc-mandatory-compliance-rate'] },
  { key: 'overall-quality', title: 'Overall quality', metricKeys: ['bbc-lowest-pillar', 'bbc-frequency-score-trend', 'bbc-overdue-action-trend'] },
  { key: 'mandatory-compliance', title: 'Mandatory compliance', metricKeys: ['bbc-mandatory-compliance-summary'] },
  { key: 'pillar-performance-risks', title: 'Pillar performance and risks', metricKeys: ['bbc-pillar-performance', 'bbc-lowest-pillars', 'bbc-repeated-findings'] },
  { key: 'actions', title: 'Actions', metricKeys: ['bbc-action-attention', 'bbc-open-action-summary'] },
  { key: 'benchmarking', title: 'Benchmarking', metricKeys: ['bbc-local-benchmark'] }
];

const BLUEBIRD_EXECUTIVE_DASHBOARD_SECTIONS: BluebirdDashboardSection[] = [
  {
    key: 'network-overview',
    title: 'Network overview',
    metricKeys: [
      'bbc-exec-overall-quality',
      'bbc-exec-compliance',
      'bbc-exec-improvement-priorities'
    ]
  },
  {
    key: 'network-health',
    title: 'Network health',
    metricKeys: [
      'bbc-exec-network-health',
      'bbc-exec-offices-meeting-standard',
      'bbc-exec-offices-requiring-improvement'
    ]
  },
  {
    key: 'pillar-performance',
    title: 'Pillar performance',
    metricKeys: [
      'bbc-exec-highest-pillar',
      'bbc-exec-lowest-pillar'
    ]
  },
  {
    key: 'office-benchmarking',
    title: 'Office benchmarking',
    metricKeys: ['bbc-exec-office-benchmark']
  },
  {
    key: 'network-trends',
    title: 'Network trends',
    metricKeys: [
      'bbc-exec-monthly-quality-trend',
      'bbc-exec-ytd-comparison',
      'bbc-exec-yoy-comparison'
    ]
  },
  {
    key: 'regional-comparison',
    title: 'Regional comparison',
    metricKeys: ['bbc-exec-regional-comparison']
  }
];

@Component({
  selector: 'app-dashboard-page',
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DashboardPageComponent implements OnInit {
  private readonly customerContext = inject(CustomerContextService);
  @ViewChild('drilldownRegion') drilldownRegion?: ElementRef<HTMLElement>;

  private readonly initialDateRange = dashboardDateRange(DEFAULT_DASHBOARD_DATE_RANGE);

  public dashboards: DashboardSummary[] = [];
  public selectedDashboardId = -1;
  public filters: DashboardFilterState = {
    StartDate: this.initialDateRange.StartDate,
    EndDate: this.initialDateRange.EndDate,
    LocationID: null,
    AuditTypeID: null,
    AuditID: null,
    FrequencyID: null,
    LocationName: 'All Locations',
    AuditType: this.customerContext.displayText('All Audit Types'),
    AuditName: this.customerContext.displayText('All Audits'),
    IncludeSublocations: true
  };

  public widgets: DashboardWidgetResult[] = [];
  public isLoading = true;
  public errorMessage = '';
  public selectionPrompt = '';
  public filtersPending = false;
  public lastUpdatedDisplay = '';
  public selectedWidget: DashboardWidgetResult | null = null;
  public selectedDrilldownSelection: DashboardWidgetSelection | null = null;
  public drilldown: DashboardWidgetDrilldownResponse | null = null;
  public drilldownLoading = false;
  public drilldownPageNumber = 1;
  public readonly drilldownPageSize = 5;
  public drilldownSortField: keyof DashboardWidgetDrilldownRow = 'DateDisplay';
  public drilldownSortDirection: DashboardSortDirection = 'desc';
  public editMode = false;
  public editModeMessage = '';
  public filtersExpanded = false;
  public locationFilterOptions: DashboardFilterOption[] = [{ ID: null, Label: 'All Locations' }];
  public auditTypeFilterOptions: DashboardFilterOption[] = [{ ID: null, Label: this.customerContext.displayText('All Audit Types') }];
  public auditFilterOptions: DashboardFilterOption[] = [{ ID: null, Label: this.customerContext.displayText('All Audits') }];
  public frequencyFilterOptions: DashboardFilterOption[] = [{ ID: null, Label: 'All Frequencies' }];
  public showOfficeSelector = true;
  public officeSelectionRequired = false;
  public scopeFilterOptions: DashboardScopeOption[] = [];
  public showScopeSelector = false;
  public effectiveScope: DashboardScopeResolution | null = null;
  public showAuditFilters = true;
  public qualityManagerFilterOptions: DashboardPortfolioFilterOption[] = [];
  public businessDevelopmentManagerFilterOptions: DashboardPortfolioFilterOption[] = [];
  public showQualityManagerSelector = false;
  public showBusinessDevelopmentManagerSelector = false;
  public portfolioFilterPreviewRequested = false;
  public usingTemporaryPortfolioFilters = false;
  public filterNotice = '';
  public metricCatalogue: DashboardMetricDefinition[] = [];
  public addWidgetPanelVisible = false;
  public selectedMetricDefinitionID = '';
  public editModeSaving = false;
  public renameDialogVisible = false;
  public renameTitle = '';
  public renameSubtitle = '';
  public renameFooterText = '';
  public renameIcon = '';
  public renameErrorMessage = '';
  public renameTargetWidget: DashboardWidgetResult | null = null;
  public readonly widgetIconOptions = [
    { icon: 'analytics', label: 'Analytics' },
    { icon: 'query_stats', label: 'Trend' },
    { icon: 'bar_chart', label: 'Bar chart' },
    { icon: 'donut_large', label: 'Doughnut' },
    { icon: 'fact_check', label: 'Checklist' },
    { icon: 'task_alt', label: 'Completed' },
    { icon: 'priority_high', label: 'Priority' },
    { icon: 'warning', label: 'Warning' },
    { icon: 'schedule', label: 'Schedule' },
    { icon: 'calendar_month', label: 'Calendar' },
    { icon: 'groups', label: 'People' },
    { icon: 'location_on', label: 'Location' }
  ];
  public isBluebirdDemo = false;
  public summaryPagingWidgetIDs = new Set<number>();
  private readonly lastDashboardStorageKey = 'careAudits.lastDashboardId';
  private drilldownTriggerElement: HTMLElement | null = null;
  private requestedDashboardId: number | null = null;
  private returningHome = false;

  constructor(
    private dashboardService: DashboardService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.isBluebirdDemo = !environment.production
      && environment.blueBird
      && this.route.snapshot.queryParamMap?.get('demo') === 'bluebird';
    this.portfolioFilterPreviewRequested = !environment.production && environment.blueBird;

    if (this.isBluebirdDemo) {
      this.initialiseBluebirdDemo();
      if (this.route.snapshot.queryParamMap?.get('drilldown') === 'actions') {
        this.openBluebirdDemoActionDrilldown();
      }
      return;
    }

    const routeDashboardId = Number(this.route.snapshot.paramMap.get('dashboardId'));
    if (Number.isFinite(routeDashboardId) && routeDashboardId !== 0) {
      this.requestedDashboardId = routeDashboardId;
      this.selectedDashboardId = routeDashboardId;
    } else {
      const savedDashboardId = this.getLastDashboardId();
      if (savedDashboardId !== null) {
        this.selectedDashboardId = savedDashboardId;
      }
    }

    this.loadDashboards();
    this.loadMetricCatalogue();
  }

  loadPermissionFilters(loadDashboardAfter = false): void {
    this.selectionPrompt = '';
    this.filtersPending = false;
    this.dashboardService.getFilterOptions(this.selectedDashboardId).subscribe({
      next: result => {
        if (result.IsError) {
          if (this.shouldReturnHome(result.ErrorCode)) {
            this.returnToHome();
            return;
          }
          this.errorMessage = result.ErrorMessage || 'Dashboard filters could not be loaded.';
          this.isLoading = false;
          return;
        }

        this.errorMessage = '';
        this.selectionPrompt = '';
        this.filtersPending = false;
        this.setPermissionFilterOptions(result);
        if (this.locationFilterOptions.length === 0 && this.scopeFilterOptions.length === 0) {
          this.selectionPrompt = 'You do not currently have access to any locations for this dashboard.';
          this.widgets = [];
          this.isLoading = false;
          return;
        }
        if (this.officeSelectionRequired && this.filters.LocationID == null) {
          this.selectionPrompt = this.locationSelectionPrompt;
          this.widgets = [];
          this.filtersExpanded = true;
          this.isLoading = false;
          return;
        }
        if (loadDashboardAfter) {
          this.loadDashboardData();
        }
      },
      error: () => this.returnToHome()
    });
  }

  private setPermissionFilterOptions(filters: DashboardFilterOptionsResponse): void {
    this.showOfficeSelector = this.isLocalBluebirdDashboard
      ? (filters.Offices || []).length > 0
      : filters.ShowOfficeSelector;
    this.officeSelectionRequired = filters.OfficeSelectionRequired;
    this.scopeFilterOptions = filters.Scopes || [];
    this.showScopeSelector = !!filters.ShowScopeSelector;
    this.effectiveScope = filters.EffectiveScope || null;
    this.showAuditFilters = filters.ShowAuditFilters !== false && !this.usesSimplifiedDashboardFilters;
    const apiQualityManagers = this.normalisePortfolioOptions(filters.QualityManagers);
    const apiBusinessDevelopmentManagers = this.normalisePortfolioOptions(filters.BusinessDevelopmentManagers);
    this.usingTemporaryPortfolioFilters = this.isExecutiveBluebirdDashboard
      && this.portfolioFilterPreviewRequested
      && (apiQualityManagers.length === 0 || filters.ShowQualityManagerSelector !== true)
      && (apiBusinessDevelopmentManagers.length === 0
        || filters.ShowBusinessDevelopmentManagerSelector !== true);
    this.qualityManagerFilterOptions = this.isExecutiveBluebirdDashboard
      ? (this.usingTemporaryPortfolioFilters
        ? this.normalisePortfolioOptions(bluebirdDemoFilters.QualityManagers)
        : apiQualityManagers)
      : [];
    this.businessDevelopmentManagerFilterOptions = this.isExecutiveBluebirdDashboard
      ? (this.usingTemporaryPortfolioFilters
        ? this.normalisePortfolioOptions(bluebirdDemoFilters.BusinessDevelopmentManagers)
        : apiBusinessDevelopmentManagers)
      : [];
    this.showQualityManagerSelector = this.isExecutiveBluebirdDashboard
      && (this.usingTemporaryPortfolioFilters || filters.ShowQualityManagerSelector === true)
      && this.qualityManagerFilterOptions.length > 0;
    this.showBusinessDevelopmentManagerSelector = this.isExecutiveBluebirdDashboard
      && (this.usingTemporaryPortfolioFilters || filters.ShowBusinessDevelopmentManagerSelector === true)
      && this.businessDevelopmentManagerFilterOptions.length > 0;
    this.ensureSelectedPortfolioOptions();
    this.filterNotice = this.usesSimplifiedDashboardFilters ? '' : (filters.FilterNotice || '');
    if (this.scopeFilterOptions.length > 0) {
      const selectedScope = this.scopeFilterOptions.find(option =>
        option.ScopeType === this.filters.ScopeType && (option.ScopeID ?? null) === (this.filters.ScopeID ?? null))
        ?? this.highestAvailableScope();
      this.filters.ScopeType = selectedScope.ScopeType;
      this.filters.ScopeID = selectedScope.ScopeID ?? null;
      if (this.effectiveScope
          && (this.effectiveScope.EffectiveScopeType !== selectedScope.ScopeType
            || (this.effectiveScope.EffectiveScopeID ?? null) !== (selectedScope.ScopeID ?? null))) {
        this.effectiveScope = {
          ...this.effectiveScope,
          RequestedScopeType: selectedScope.ScopeType,
          RequestedScopeID: selectedScope.ScopeID ?? null,
          EffectiveScopeType: selectedScope.ScopeType,
          EffectiveScopeID: selectedScope.ScopeID ?? null,
          EffectiveScopeLabel: selectedScope.Label,
          TotalOfficeCount: selectedScope.OfficeCount
        };
      }
    } else {
      this.filters.ScopeType = undefined;
      this.filters.ScopeID = undefined;
    }
    this.locationFilterOptions = this.uniqueSortedOptions(filters.Offices || []);
    this.auditTypeFilterOptions = this.uniqueSortedOptions([
      { ID: null, Label: this.customerContext.displayText('All Audit Types') },
      ...(filters.AuditTypes || [])
    ]);
    this.auditFilterOptions = this.uniqueSortedOptions([
      { ID: null, Label: this.customerContext.displayText('All Audits') },
      ...(filters.Audits || [])
    ]);
    this.frequencyFilterOptions = this.uniqueSortedOptions([
      { ID: null, Label: 'All Frequencies' },
      ...(filters.Frequencies || [])
    ]);

    const selectedOfficeID = filters.SelectedOfficeID
      ?? (this.locationFilterOptions.some(option => option.ID === this.filters.LocationID) ? this.filters.LocationID : null);
    if (selectedOfficeID == null) {
      this.filters.LocationID = null;
      this.filters.LocationName = '';
    } else {
      this.applyFilterOption('LocationID', 'LocationName', this.locationFilterOptions, selectedOfficeID);
    }
    if (!this.showAuditFilters) {
      this.filters.AuditTypeID = null;
      this.filters.AuditType = this.customerContext.displayText('All Audit Types');
      this.filters.AuditID = null;
      this.filters.AuditName = this.customerContext.displayText('All Audits');
      this.filters.FrequencyID = null;
    } else {
      this.ensureSelectedFilterOption('AuditTypeID', 'AuditType', this.auditTypeFilterOptions);
      this.ensureSelectedFilterOption('AuditID', 'AuditName', this.auditFilterOptions);
      if (!this.frequencyFilterOptions.some(option => option.ID === this.filters.FrequencyID)) {
        this.filters.FrequencyID = null;
      }
    }
  }

  private highestAvailableScope(): DashboardScopeOption {
    const scopePriority: Record<DashboardScopeOption['ScopeType'], number> = {
      network: 0,
      region: 1,
      office: 2
    };
    return [...this.scopeFilterOptions]
      .sort((left, right) => scopePriority[left.ScopeType] - scopePriority[right.ScopeType])[0];
  }

  private uniqueSortedOptions(options: DashboardFilterOption[]): DashboardFilterOption[] {
    const unique = new Map<string, DashboardFilterOption>();
    options
      .filter(option => !!option.Label?.trim())
      .forEach(option => {
        const normalised = { ...option, Label: option.Label.trim() };
        unique.set(option.ID === null ? 'all' : `id:${option.ID}`, normalised);
      });

    const allOption = unique.get('all');
    const concreteOptions = Array.from(unique.entries())
      .filter(([key]) => key !== 'all')
      .map(([, option]) => option)
      .sort((a, b) => a.Label.localeCompare(b.Label, undefined, { sensitivity: 'base' }));

    return allOption ? [allOption, ...concreteOptions] : concreteOptions;
  }

  private normalisePortfolioOptions(options?: DashboardPortfolioFilterOption[]): DashboardPortfolioFilterOption[] {
    const unique = new Map<number, DashboardPortfolioFilterOption>();
    (options ?? [])
      .filter(option => Number.isFinite(option.ID) && !!option.Label?.trim())
      .forEach(option => unique.set(option.ID, {
        ...option,
        Label: option.Label.trim(),
        RelatedOptionIDs: [...new Set(option.RelatedOptionIDs ?? [])]
      }));

    return Array.from(unique.values()).sort((left, right) => {
      if (!!left.IsUnassigned !== !!right.IsUnassigned) {
        return left.IsUnassigned ? 1 : -1;
      }
      return left.Label.localeCompare(right.Label, undefined, { sensitivity: 'base' });
    });
  }

  private ensureSelectedPortfolioOptions(): void {
    if (!this.showQualityManagerSelector
        || !this.qualityManagerFilterOptions.some(option => option.ID === this.filters.QualityManagerID)) {
      this.filters.QualityManagerID = null;
    }
    if (!this.showBusinessDevelopmentManagerSelector
        || !this.businessDevelopmentManagerFilterOptions.some(option => option.ID === this.filters.BusinessDevelopmentManagerID)) {
      this.filters.BusinessDevelopmentManagerID = null;
    }
    this.clearInvalidRelatedPortfolioSelection();
  }

  get visibleQualityManagerFilterOptions(): DashboardPortfolioFilterOption[] {
    return this.relatedPortfolioOptions(
      this.qualityManagerFilterOptions,
      this.filters.BusinessDevelopmentManagerID);
  }

  get visibleBusinessDevelopmentManagerFilterOptions(): DashboardPortfolioFilterOption[] {
    return this.relatedPortfolioOptions(
      this.businessDevelopmentManagerFilterOptions,
      this.filters.QualityManagerID);
  }

  private relatedPortfolioOptions(
    options: DashboardPortfolioFilterOption[],
    relatedID?: number | null
  ): DashboardPortfolioFilterOption[] {
    if (relatedID == null) {
      return options;
    }
    return options.filter(option => (option.RelatedOptionIDs ?? []).includes(relatedID));
  }

  onQualityManagerFilterChange(qualityManagerID: number | null): void {
    this.filters.QualityManagerID = qualityManagerID;
    if (this.filters.BusinessDevelopmentManagerID != null
        && !this.visibleBusinessDevelopmentManagerFilterOptions.some(
          option => option.ID === this.filters.BusinessDevelopmentManagerID)) {
      this.filters.BusinessDevelopmentManagerID = null;
    }
    this.markFiltersPending();
  }

  onBusinessDevelopmentManagerFilterChange(businessDevelopmentManagerID: number | null): void {
    this.filters.BusinessDevelopmentManagerID = businessDevelopmentManagerID;
    if (this.filters.QualityManagerID != null
        && !this.visibleQualityManagerFilterOptions.some(
          option => option.ID === this.filters.QualityManagerID)) {
      this.filters.QualityManagerID = null;
    }
    this.markFiltersPending();
  }

  portfolioOptionLabel(option: DashboardPortfolioFilterOption): string {
    return option.IsActive === false ? `${option.Label} (Inactive)` : option.Label;
  }

  private clearInvalidRelatedPortfolioSelection(): void {
    if (this.filters.QualityManagerID != null
        && this.filters.BusinessDevelopmentManagerID != null
        && !this.visibleBusinessDevelopmentManagerFilterOptions.some(
          option => option.ID === this.filters.BusinessDevelopmentManagerID)) {
      this.filters.BusinessDevelopmentManagerID = null;
    }
  }

  private ensureSelectedFilterOption(
    idKey: 'LocationID' | 'AuditTypeID' | 'AuditID',
    labelKey: 'LocationName' | 'AuditType' | 'AuditName',
    options: DashboardFilterOption[]
  ): void {
    const selected = options.find(option => option.ID === this.filters[idKey])
      ?? options.find(option => option.Label === this.filters[labelKey])
      ?? options[0];

    this.filters[idKey] = selected?.ID ?? null;
    this.filters[labelKey] = selected?.Label ?? '';
  }

  onLocationFilterChange(locationID: number | null): void {
    this.applyFilterOption('LocationID', 'LocationName', this.locationFilterOptions, locationID);
    if (locationID != null) {
      this.markFiltersPending();
    } else {
      this.filtersPending = false;
      this.selectionPrompt = this.locationSelectionPrompt;
    }
  }

  get selectedScopeKey(): string {
    return this.filters.ScopeType
      ? `${this.filters.ScopeType}:${this.filters.ScopeID ?? ''}`
      : '';
  }

  onScopeFilterChange(scopeKey: string): void {
    const selected = this.scopeFilterOptions.find(option =>
      `${option.ScopeType}:${option.ScopeID ?? ''}` === scopeKey);
    this.filters.ScopeType = selected?.ScopeType;
    this.filters.ScopeID = selected?.ScopeID ?? null;
    if (selected) {
      this.effectiveScope = {
        ...(this.effectiveScope || {
          DashboardScopeType: selected.ScopeType,
          RequestedScopeType: selected.ScopeType,
          RootLocationIDs: [],
          RegionLocationIDs: [],
          OfficeLocationIDs: [],
          LocationScopeIDs: [],
          AvailableScopes: this.scopeFilterOptions,
          TotalOfficeCount: selected.OfficeCount,
          ContributingOfficeCount: 0
        }),
        RequestedScopeType: selected.ScopeType,
        RequestedScopeID: selected.ScopeID ?? null,
        EffectiveScopeType: selected.ScopeType,
        EffectiveScopeID: selected.ScopeID ?? null,
        EffectiveScopeLabel: selected.Label,
        TotalOfficeCount: selected.OfficeCount
      };
      this.markFiltersPending();
    }
  }

  onAuditTypeFilterChange(auditTypeID: number | null): void {
    this.applyFilterOption('AuditTypeID', 'AuditType', this.auditTypeFilterOptions, auditTypeID);
  }

  onAuditFilterChange(auditID: number | null): void {
    this.applyFilterOption('AuditID', 'AuditName', this.auditFilterOptions, auditID);
  }

  onFrequencyFilterChange(frequencyID: number | null): void {
    const selected = this.frequencyFilterOptions.find(option => option.ID === frequencyID)
      ?? this.frequencyFilterOptions[0];
    this.filters.FrequencyID = selected?.ID ?? null;
  }

  private applyFilterOption(
    idKey: 'LocationID' | 'AuditTypeID' | 'AuditID',
    labelKey: 'LocationName' | 'AuditType' | 'AuditName',
    options: DashboardFilterOption[],
    selectedID: number | null
  ): void {
    const selected = options.find(option => option.ID === selectedID) ?? options[0];
    this.filters[idKey] = selected?.ID ?? null;
    this.filters[labelKey] = selected?.Label ?? '';
  }

  loadDashboards(): void {
    this.dashboardService.getDashboards().subscribe({
      next: dashboards => {
        if (dashboards.length === 0) {
          this.returnToHome('You do not currently have access to a dashboard.');
          return;
        }

        this.dashboards = dashboards;
        const preferredExecutiveDashboard = this.dashboards.find(dashboard =>
          dashboard.DashboardID > 0
          && dashboard.DashboardScopeType === 'network');
        const preferredBluebirdDashboard = environment.blueBird
          ? this.dashboards.find(dashboard =>
              dashboard.DashboardID > 0
              && dashboard.Name === BLUEBIRD_DASHBOARD_NAME
              && dashboard.IsSharedTemplate
              && dashboard.IsLocked)
          : undefined;
        const selectedDashboardExists = this.dashboards.some(d => d.DashboardID === this.selectedDashboardId);

        if (this.requestedDashboardId !== null && !selectedDashboardExists) {
          this.returnToHome('That dashboard is no longer available to you.');
          return;
        }

        if (this.requestedDashboardId === null && preferredExecutiveDashboard) {
          this.selectedDashboardId = preferredExecutiveDashboard.DashboardID;
          this.rememberLastDashboardId(this.selectedDashboardId);
        } else if (preferredBluebirdDashboard && (!selectedDashboardExists || this.selectedDashboardId < 0)) {
          this.selectedDashboardId = preferredBluebirdDashboard.DashboardID;
          this.rememberLastDashboardId(this.selectedDashboardId);
        } else if (!selectedDashboardExists) {
          this.selectedDashboardId = this.dashboards.find(d => d.IsDefault)?.DashboardID
            ?? this.dashboards[0]?.DashboardID
            ?? -1;
        }
        this.loadPermissionFilters(true);
      },
      error: () => this.returnToHome()
    });
  }

  loadMetricCatalogue(): void {
    this.dashboardService.getMetricCatalogue().pipe(
      tap(metrics => {
        this.metricCatalogue = metrics;
        this.selectedMetricDefinitionID = this.selectedMetricDefinitionID || metrics[0]?.MetricDefinitionKey || '';
      })
    ).subscribe();
  }

  loadDashboardData(): void {
    if (this.isBluebirdDemo) {
      const response = buildBluebirdDemoDashboardData();
      this.errorMessage = '';
      this.selectionPrompt = '';
      this.filtersPending = false;
      this.closeDrilldown();
      this.widgets = this.normaliseWidgetDisplay(response.Widgets);
      this.lastUpdatedDisplay = response.LastUpdatedDisplay;
      this.isLoading = false;
      return;
    }

    if (this.officeSelectionRequired && this.filters.LocationID == null) {
      this.errorMessage = '';
      this.selectionPrompt = this.locationSelectionPrompt;
      this.filtersPending = false;
      this.filtersExpanded = true;
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.selectionPrompt = '';
    this.filtersPending = false;
    this.widgets = [];
    this.closeDrilldown();

    const request: DashboardDataRequest = {
      DashboardID: this.selectedDashboardId,
      Filters: this.requestFilters
    };

    this.dashboardService.getDashboardData(request).pipe(
      tap(response => {
        if (response.IsError) {
          if (this.shouldReturnHome(response.ErrorCode)) {
            this.returnToHome();
            return;
          }
          this.errorMessage = response.ErrorMessage || 'Dashboard data could not be loaded.';
          this.lastUpdatedDisplay = '';
          this.isLoading = false;
          return;
        }
        this.widgets = this.normaliseWidgetDisplay(response.Widgets);
        this.lastUpdatedDisplay = response.LastUpdatedDisplay;
        this.isLoading = false;
        this.rememberLastDashboardId(this.selectedDashboardId);
      })
    ).subscribe({
      error: () => this.returnToHome()
    });
  }

  onDashboardChange(): void {
    this.editMode = false;
    this.addWidgetPanelVisible = false;
    this.closeRenameDialog();
    this.rememberLastDashboardId(this.selectedDashboardId);
    this.filters.LocationID = null;
    this.filters.LocationName = '';
    this.filters.ScopeType = undefined;
    this.filters.ScopeID = undefined;
    this.filters.QualityManagerID = null;
    this.filters.BusinessDevelopmentManagerID = null;
    this.errorMessage = '';
    this.selectionPrompt = '';
    this.filtersPending = false;
    this.widgets = [];
    this.isLoading = true;
    this.loadPermissionFilters(true);
  }

  dashboardDisplayName(dashboard: DashboardSummary): string {
    return dashboard.DashboardID < 0
      ? `Standard - ${dashboard.Name}`
      : dashboard.Name;
  }

  changeSummaryPage(page: DashboardSummaryPageRequest): void {
    if (this.isBluebirdDemo || this.summaryPagingWidgetIDs.has(page.DashboardWidgetID)) {
      return;
    }

    this.summaryPagingWidgetIDs.add(page.DashboardWidgetID);
    const request: DashboardDataRequest = {
      DashboardID: this.selectedDashboardId,
      Filters: this.requestFilters,
      SummaryPaging: [page]
    };

    this.dashboardService.getDashboardData(request).subscribe({
      next: response => {
        if (!response.IsError) {
          const updatedWidget = this.normaliseWidgetDisplay(response.Widgets)
            .find(widget => widget.DashboardWidgetID === page.DashboardWidgetID);
          if (updatedWidget) {
            this.widgets = this.widgets.map(widget =>
              widget.DashboardWidgetID === page.DashboardWidgetID ? updatedWidget : widget);
            this.lastUpdatedDisplay = response.LastUpdatedDisplay;
          }
        }
        this.summaryPagingWidgetIDs.delete(page.DashboardWidgetID);
      },
      error: () => this.summaryPagingWidgetIDs.delete(page.DashboardWidgetID)
    });
  }

  get usesBluebirdGroupedLayout(): boolean {
    return this.widgets.some(widget => widget.MetricDefinitionKey.startsWith('bbc-'));
  }

  get bluebirdWidgetSections(): { key: string; title: string; widgets: DashboardWidgetResult[] }[] {
    const widgetsByMetricKey = new Map(
      this.widgets.map(widget => [widget.MetricDefinitionKey, widget] as const)
    );

    const sectionMetadata = this.widgets.some(widget => widget.MetricDefinitionKey.startsWith('bbc-exec-'))
      ? BLUEBIRD_EXECUTIVE_DASHBOARD_SECTIONS
      : BLUEBIRD_DASHBOARD_SECTIONS;

    return sectionMetadata
      .map(section => ({
        key: section.key,
        title: section.title,
        widgets: section.metricKeys
          .map(metricKey => widgetsByMetricKey.get(metricKey))
          .filter((widget): widget is DashboardWidgetResult => !!widget)
      }))
      .filter(section => section.widgets.length > 0);
  }

  applyFilters(): void {
    if (this.officeSelectionRequired && this.filters.LocationID == null) {
      this.errorMessage = '';
      this.selectionPrompt = this.locationSelectionPrompt;
      this.filtersPending = false;
      this.filtersExpanded = true;
      return;
    }
    this.selectionPrompt = '';
    this.filtersPending = false;
    this.filtersExpanded = false;
    this.loadDashboardData();
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  get filterSummaryItems(): { label: string; value: string }[] {
    if (this.isBluebirdDemo) {
      return [
        { label: 'Period', value: `${this.formatDateDisplay(this.filters.StartDate)} to ${this.formatDateDisplay(this.filters.EndDate)}` },
        { label: 'Location', value: this.filters.LocationName },
        { label: 'Coverage', value: '18 offices' },
        ...this.portfolioFilterSummaryItems
      ];
    }

    const selectedScope = this.scopeFilterOptions.find(option =>
      option.ScopeType === this.filters.ScopeType
      && (option.ScopeID ?? null) === (this.filters.ScopeID ?? null));
    const locationOrScope = this.scopeFilterOptions.length > 0
      ? { label: 'Scope', value: selectedScope?.Label || this.effectiveScope?.EffectiveScopeLabel || 'Network' }
      : { label: 'Office', value: this.filters.LocationName || 'Please select' };
    const portfolioItems = this.portfolioFilterSummaryItems;
    if (this.usesSimplifiedDashboardFilters) {
      return [locationOrScope, ...portfolioItems];
    }
    const items = [
      { label: 'Period', value: `${this.formatDateDisplay(this.filters.StartDate)} to ${this.formatDateDisplay(this.filters.EndDate)}` },
      locationOrScope
    ];
    if (this.scopeFilterOptions.length > 0) {
      return [...items, ...portfolioItems];
    }
    return [
      ...items,
      { label: this.customerContext.displayText('Audit type'), value: this.filters.AuditType || this.customerContext.displayText('All Audit Types') },
      { label: this.customerContext.displayText('Audit'), value: this.filters.AuditName || this.customerContext.displayText('All Audits') },
      {
        label: 'Frequency',
        value: this.frequencyFilterOptions.find(option => option.ID === this.filters.FrequencyID)?.Label || 'All Frequencies'
      },
      { label: 'Sublocations', value: this.filters.IncludeSublocations ? 'Included' : 'Excluded' },
      ...portfolioItems
    ];
  }

  private get portfolioFilterSummaryItems(): { label: string; value: string }[] {
    const items: { label: string; value: string }[] = [];
    if (this.showQualityManagerSelector) {
      const selected = this.qualityManagerFilterOptions.find(option => option.ID === this.filters.QualityManagerID);
      items.push({ label: 'Regional quality assurance lead', value: selected ? this.portfolioOptionLabel(selected) : 'All' });
    }
    if (this.showBusinessDevelopmentManagerSelector) {
      const selected = this.businessDevelopmentManagerFilterOptions.find(
        option => option.ID === this.filters.BusinessDevelopmentManagerID);
      items.push({ label: 'Business development manager', value: selected ? this.portfolioOptionLabel(selected) : 'All' });
    }
    return items;
  }

  private formatDateDisplay(value?: string): string {
    if (!value) {
      return 'Not set';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private getLastDashboardId(): number | null {
    try {
      const value = window.localStorage.getItem(this.lastDashboardStorageKey);
      if (!value) {
        return null;
      }

      const dashboardID = Number(value);
      return Number.isFinite(dashboardID) && dashboardID !== 0 ? dashboardID : null;
    } catch {
      return null;
    }
  }

  private rememberLastDashboardId(dashboardID: number): void {
    try {
      window.localStorage.setItem(this.lastDashboardStorageKey, dashboardID.toString());
    } catch {
      // Ignore storage failures; dashboard selection still works for the session.
    }
  }

  toggleEditMode(): void {
    if (!this.canEnterEditMode) {
      return;
    }
    this.editMode = !this.editMode;
    this.editModeMessage = this.editMode
      ? 'Edit mode is active. Copy a standard dashboard before customising it.'
      : '';
    this.addWidgetPanelVisible = false;
    this.closeRenameDialog();
  }

  addWidgetPreview(): void {
    if (!this.selectedDashboardCapabilities.CanManageWidgets) {
      return;
    }
    this.addWidgetPanelVisible = !this.addWidgetPanelVisible;
    this.editModeMessage = this.addWidgetPanelVisible
      ? 'Choose a metric to add to this dashboard.'
      : '';
  }

  removeWidgetPreview(widget: DashboardWidgetResult): void {
    if (!this.selectedDashboardCapabilities.CanManageWidgets) {
      return;
    }
    this.removeWidgetFromDashboard(widget, true);
  }

  private removeWidgetFromDashboard(widget: DashboardWidgetResult, allowCopyFallback: boolean): void {
    this.editModeSaving = true;
    this.dashboardService.removeWidget(this.selectedDashboardId, widget.DashboardWidgetID).subscribe({
      next: result => {
        if (result.IsError) {
          if (allowCopyFallback && this.shouldCopyBeforeEdit(result.ErrorMessage)) {
            this.copyDashboardThenRemoveWidget(widget);
            return;
          }

          this.editModeSaving = false;
          this.editModeMessage = result.ErrorMessage || 'Widget could not be removed. Copy the dashboard before customising it.';
          return;
        }

        this.editModeSaving = false;
        this.widgets = this.widgets.filter(w => w.DashboardWidgetID !== widget.DashboardWidgetID);
        this.editModeMessage = `${widget.Title} removed.`;
        if (this.selectedWidget?.DashboardWidgetID === widget.DashboardWidgetID) {
          this.closeDrilldown();
        }
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Widget could not be removed.';
      }
    });
  }

  private copyDashboardThenRemoveWidget(widget: DashboardWidgetResult): void {
    const dashboardName = this.dashboards.find(d => d.DashboardID === this.selectedDashboardId)?.Name || 'Dashboard';
    const sourceWidgets = [...this.widgets];
    this.editModeMessage = 'Creating an editable dashboard copy before removing the widget.';

    this.dashboardService.copyDashboard({
      SourceDashboardID: this.selectedDashboardId,
      DashboardName: `${dashboardName} custom`
    }).subscribe({
      next: result => {
        if (result.IsError || result.DashboardID <= 0) {
          this.editModeSaving = false;
          this.editModeMessage = result.ErrorMessage || 'Dashboard could not be copied before removing the widget.';
          return;
        }

        this.selectedDashboardId = result.DashboardID;
        this.refreshDashboardListOnly();
        this.loadCopiedDashboardAndRemoveWidget(widget, sourceWidgets);
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Dashboard could not be copied before removing the widget.';
      }
    });
  }

  private loadCopiedDashboardAndRemoveWidget(sourceWidget: DashboardWidgetResult, sourceWidgets: DashboardWidgetResult[]): void {
    this.dashboardService.getDashboardData({
      DashboardID: this.selectedDashboardId,
      Filters: this.requestFilters
    }).subscribe({
      next: response => {
        this.widgets = this.normaliseWidgetDisplay(response.Widgets);
        this.lastUpdatedDisplay = response.LastUpdatedDisplay;

        if (this.widgets.length === 0) {
          this.seedCopiedDashboardWidgets(sourceWidget, sourceWidgets);
          return;
        }

        const copiedWidget = this.widgets.find(widget =>
          widget.MetricDefinitionKey === sourceWidget.MetricDefinitionKey && widget.Title === sourceWidget.Title
        ) || this.widgets.find(widget => widget.MetricDefinitionKey === sourceWidget.MetricDefinitionKey);

        if (!copiedWidget) {
          this.editModeSaving = false;
          this.editModeMessage = 'Dashboard was copied, but the matching widget could not be found.';
          return;
        }

        this.removeWidgetFromDashboard(copiedWidget, false);
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Dashboard was copied, but the copied widgets could not be loaded.';
      }
    });
  }

  private seedCopiedDashboardWidgets(sourceWidget: DashboardWidgetResult, sourceWidgets: DashboardWidgetResult[]): void {
    const widgetsToKeep = sourceWidgets.filter(widget => widget.DashboardWidgetID !== sourceWidget.DashboardWidgetID);

    if (widgetsToKeep.length === 0) {
      this.widgets = [];
      this.editModeSaving = false;
      this.editModeMessage = `${sourceWidget.Title} removed.`;
      this.closeDrilldown();
      return;
    }

    const saveRequests = widgetsToKeep.map((widget, index) =>
      this.dashboardService.addWidget(this.selectedDashboardId, this.toWidgetSaveRequest(widget, index + 1))
    );

    forkJoin(saveRequests).subscribe({
      next: results => {
        const failedResult = results.find(result => result.IsError);
        if (failedResult) {
          this.editModeSaving = false;
          this.editModeMessage = failedResult.ErrorMessage || 'Dashboard was copied, but widgets could not be saved.';
          return;
        }

        this.editModeSaving = false;
        this.editModeMessage = `${sourceWidget.Title} removed.`;
        this.closeDrilldown();
        this.loadDashboardData();
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Dashboard was copied, but widgets could not be saved.';
      }
    });
  }

  private toWidgetSaveRequest(widget: DashboardWidgetResult, seq: number): DashboardWidgetSaveRequest {
    return {
      DashboardID: this.selectedDashboardId,
      MetricDefinitionID: widget.MetricDefinitionKey,
      Title: widget.Title,
      Subtitle: widget.Subtitle,
      VisualType: widget.VisualType,
      PositiveOutcomeDirection: widget.PositiveOutcomeDirection || 'higher',
      ConfigJson: this.buildWidgetConfigJson(widget, widget.FooterText || '', widget.Icon || 'analytics'),
      LayoutJson: JSON.stringify({
        displayOrder: seq,
        size: widget.Size || 'medium'
      }),
      Seq: seq
    };
  }

  private refreshDashboardListOnly(): void {
    this.dashboardService.getDashboards().subscribe({
      next: dashboards => {
        this.dashboards = dashboards;
      }
    });
  }

  private shouldCopyBeforeEdit(errorMessage?: string): boolean {
    if (!this.selectedDashboardCapabilities.CanCopyDashboard) {
      return false;
    }
    const message = (errorMessage || '').toLowerCase();
    return message.includes('not found')
      || message.includes('cannot be deleted')
      || message.includes('cannot be edited')
      || message.includes('copy the dashboard');
  }

  renameWidgetPreview(widget: DashboardWidgetResult): void {
    if (!this.selectedDashboardCapabilities.CanManageWidgets) {
      return;
    }
    this.renameTargetWidget = widget;
    this.renameTitle = widget.Title;
    this.renameSubtitle = widget.Subtitle || '';
    this.renameFooterText = widget.FooterText || '';
    this.renameIcon = widget.Icon || 'analytics';
    this.renameErrorMessage = '';
    this.renameDialogVisible = true;
  }

  cancelRenameWidget(): void {
    if (this.editModeSaving) {
      return;
    }

    this.closeRenameDialog();
  }

  confirmRenameWidget(): void {
    const widget = this.renameTargetWidget;
    const title = this.renameTitle.trim();
    const subtitle = this.renameSubtitle.trim();
    const footerText = this.renameFooterText.trim();
    const icon = this.renameIcon || 'analytics';

    if (!widget) {
      this.closeRenameDialog();
      return;
    }

    if (!title) {
      this.renameErrorMessage = 'Enter a widget title.';
      return;
    }

    if (title === widget.Title && subtitle === (widget.Subtitle || '') && footerText === (widget.FooterText || '') && icon === widget.Icon) {
      this.closeRenameDialog();
      return;
    }

    this.renameWidgetFromDashboard(widget, { title, subtitle, footerText, icon }, true);
  }

  private closeRenameDialog(): void {
    this.renameDialogVisible = false;
    this.renameTargetWidget = null;
    this.renameTitle = '';
    this.renameSubtitle = '';
    this.renameFooterText = '';
    this.renameIcon = '';
    this.renameErrorMessage = '';
  }

  private renameWidgetFromDashboard(widget: DashboardWidgetResult, update: DashboardWidgetTextUpdate, allowCopyFallback: boolean): void {
    this.editModeSaving = true;

    const request: DashboardWidgetSaveRequest = {
      DashboardWidgetID: widget.DashboardWidgetID,
      DashboardID: this.selectedDashboardId,
      MetricDefinitionID: widget.MetricDefinitionKey,
      Title: update.title,
      Subtitle: update.subtitle,
      VisualType: widget.VisualType,
      PositiveOutcomeDirection: widget.PositiveOutcomeDirection || 'higher',
      ConfigJson: this.buildWidgetConfigJson(widget, update.footerText, update.icon),
      Seq: widget.DisplayOrder || this.widgets.indexOf(widget) + 1
    };

    this.dashboardService.updateWidget(this.selectedDashboardId, widget.DashboardWidgetID, request).subscribe({
      next: result => {
        if (result.IsError) {
          if (allowCopyFallback && this.shouldCopyBeforeEdit(result.ErrorMessage)) {
            this.copyDashboardThenRenameWidget(widget, update);
            return;
          }

          this.editModeSaving = false;
          this.renameErrorMessage = result.ErrorMessage || 'Widget could not be updated.';
          this.editModeMessage = this.renameErrorMessage;
          return;
        }

        this.editModeSaving = false;
        this.editModeMessage = `${widget.Title} updated.`;
        this.closeRenameDialog();
        this.loadDashboardData();
      },
      error: () => {
        this.editModeSaving = false;
        this.renameErrorMessage = 'Widget could not be updated.';
        this.editModeMessage = this.renameErrorMessage;
      }
    });
  }

  private copyDashboardThenRenameWidget(widget: DashboardWidgetResult, update: DashboardWidgetTextUpdate): void {
    const dashboardName = this.dashboards.find(d => d.DashboardID === this.selectedDashboardId)?.Name || 'Dashboard';
    const sourceWidgets = [...this.widgets];
    this.editModeMessage = 'Creating an editable dashboard copy before updating the widget.';

    this.dashboardService.copyDashboard({
      SourceDashboardID: this.selectedDashboardId,
      DashboardName: `${dashboardName} custom`
    }).subscribe({
      next: result => {
        if (result.IsError || result.DashboardID <= 0) {
          this.editModeSaving = false;
          this.renameErrorMessage = result.ErrorMessage || 'Dashboard could not be copied before updating the widget.';
          this.editModeMessage = this.renameErrorMessage;
          return;
        }

        this.selectedDashboardId = result.DashboardID;
        this.refreshDashboardListOnly();
        this.loadCopiedDashboardAndRenameWidget(widget, update, sourceWidgets);
      },
      error: () => {
        this.editModeSaving = false;
        this.renameErrorMessage = 'Dashboard could not be copied before updating the widget.';
        this.editModeMessage = this.renameErrorMessage;
      }
    });
  }

  private loadCopiedDashboardAndRenameWidget(
    sourceWidget: DashboardWidgetResult,
    update: DashboardWidgetTextUpdate,
    sourceWidgets: DashboardWidgetResult[]
  ): void {
    this.dashboardService.getDashboardData({
      DashboardID: this.selectedDashboardId,
      Filters: this.requestFilters
    }).subscribe({
      next: response => {
        this.widgets = this.normaliseWidgetDisplay(response.Widgets);
        this.lastUpdatedDisplay = response.LastUpdatedDisplay;

        if (this.widgets.length === 0) {
          this.seedCopiedDashboardWidgetsThenRename(sourceWidget, update, sourceWidgets);
          return;
        }

        const copiedWidget = this.widgets.find(widget =>
          widget.MetricDefinitionKey === sourceWidget.MetricDefinitionKey && widget.Title === sourceWidget.Title
        ) || this.widgets.find(widget => widget.MetricDefinitionKey === sourceWidget.MetricDefinitionKey);

        if (!copiedWidget) {
          this.editModeSaving = false;
          this.renameErrorMessage = 'Dashboard was copied, but the matching widget could not be found.';
          this.editModeMessage = this.renameErrorMessage;
          return;
        }

        this.renameWidgetFromDashboard(copiedWidget, update, false);
      },
      error: () => {
        this.editModeSaving = false;
        this.renameErrorMessage = 'Dashboard was copied, but the copied widgets could not be loaded.';
        this.editModeMessage = this.renameErrorMessage;
      }
    });
  }

  private seedCopiedDashboardWidgetsThenRename(
    sourceWidget: DashboardWidgetResult,
    update: DashboardWidgetTextUpdate,
    sourceWidgets: DashboardWidgetResult[]
  ): void {
    const saveRequests = sourceWidgets.map((widget, index) => {
      const request = this.toWidgetSaveRequest(widget, index + 1);
      if (widget.DashboardWidgetID === sourceWidget.DashboardWidgetID) {
        request.Title = update.title;
        request.Subtitle = update.subtitle;
        request.ConfigJson = this.buildWidgetConfigJson(widget, update.footerText, update.icon);
      }
      return this.dashboardService.addWidget(this.selectedDashboardId, request);
    });

    forkJoin(saveRequests).subscribe({
      next: results => {
        const failedResult = results.find(result => result.IsError);
        if (failedResult) {
          this.editModeSaving = false;
          this.renameErrorMessage = failedResult.ErrorMessage || 'Dashboard was copied, but widgets could not be saved.';
          this.editModeMessage = this.renameErrorMessage;
          return;
        }

        this.editModeSaving = false;
        this.editModeMessage = `${sourceWidget.Title} updated.`;
        this.closeRenameDialog();
        this.loadDashboardData();
      },
      error: () => {
        this.editModeSaving = false;
        this.renameErrorMessage = 'Dashboard was copied, but widgets could not be saved.';
        this.editModeMessage = this.renameErrorMessage;
      }
    });
  }

  private buildWidgetConfigJson(widget: DashboardWidgetResult, footerText: string, icon: string): string {
    return JSON.stringify({
      ...(widget.Configuration || {}),
      FooterText: footerText,
      Icon: icon
    });
  }

  private normaliseWidgetDisplay(widgets: DashboardWidgetResult[]): DashboardWidgetResult[] {
    const noScheduledAudits = widgets.some(widget => this.isNoScheduledAuditsWidget(widget));

    return widgets.map(widget => {
      if (widget.MetricDefinitionKey === 'missed-audits' && noScheduledAudits) {
        return {
          ...widget,
          VisualType: 'number',
          Value: 'No scheduled audits',
          ValueSuffix: '',
          TrendText: 'Not relevant for this selection',
          TrendDirection: 'flat',
          Status: 'neutral',
          ChartData: undefined,
          FooterText: 'No scheduled audits in the selected period'
        };
      }

      if (widget.MetricDefinitionKey === 'actions-completed-on-time') {
        return {
          ...widget,
          Title: ['Actions on time', 'Actions completed on time'].includes(widget.Title)
            ? 'Completed on time'
            : widget.Title,
          Subtitle: widget.Subtitle === 'Completed actions within due date'
            ? 'Resolved actions completed by due date'
            : widget.Subtitle,
          FooterText: ['Based on resolved actions', 'Completed actions in selected date range'].includes(widget.FooterText || '')
            ? 'Of actions completed in selected date range'
            : widget.FooterText
        };
      }

      return widget;
    });
  }

  private isNoScheduledAuditsWidget(widget: DashboardWidgetResult): boolean {
    return widget.MetricDefinitionKey === 'completion-rate'
      && widget.Status === 'neutral'
      && typeof widget.Value === 'string'
      && widget.Value.toLowerCase() === 'no scheduled audits';
  }

  configureWidgetPreview(widget: DashboardWidgetResult): void {
    this.editModeMessage = `${widget.Title} can currently be updated or removed. More widget configuration controls are next.`;
  }

  copyCurrentDashboard(): void {
    if (!this.canEnterEditMode || !this.selectedDashboardCapabilities.CanCopyDashboard) {
      return;
    }
    const dashboardName = this.dashboards.find(d => d.DashboardID === this.selectedDashboardId)?.Name || 'Dashboard';
    this.editModeSaving = true;
    this.dashboardService.copyDashboard({
      SourceDashboardID: this.selectedDashboardId,
      DashboardName: `${dashboardName} custom`
    }).subscribe({
      next: result => {
        this.editModeSaving = false;
        if (result.IsError || result.DashboardID <= 0) {
          this.editModeMessage = result.ErrorMessage || 'Dashboard could not be copied.';
          return;
        }

        this.selectedDashboardId = result.DashboardID;
        this.editModeMessage = 'Dashboard copied. You can now customise this version.';
        this.loadDashboards();
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Dashboard could not be copied.';
      }
    });
  }

  addSelectedWidget(): void {
    if (!this.selectedDashboardCapabilities.CanManageWidgets) {
      return;
    }
    const metric = this.metricCatalogue.find(m => m.MetricDefinitionKey === this.selectedMetricDefinitionID);
    if (!metric) {
      this.editModeMessage = 'Choose a metric before adding a widget.';
      return;
    }

    this.saveWidget({
      DashboardID: this.selectedDashboardId,
      MetricDefinitionID: metric.MetricDefinitionKey,
      Title: metric.Name,
      Subtitle: metric.Description,
      VisualType: metric.DefaultVisualType,
      PositiveOutcomeDirection: metric.PositiveOutcomeDirection,
      Seq: this.widgets.length + 1
    }, `${metric.Name} added.`);
  }

  saveCurrentLayout(): void {
    if (!this.selectedDashboardCapabilities.CanSaveLayout) {
      return;
    }
    this.editModeSaving = true;
    this.dashboardService.saveDashboardLayout({
      DashboardID: this.selectedDashboardId,
      Widgets: this.widgets.map((widget, index) => ({
        DashboardWidgetID: widget.DashboardWidgetID,
        Seq: index + 1,
        LayoutJson: JSON.stringify({
          displayOrder: index + 1,
          size: widget.Size || 'medium'
        })
      }))
    }).subscribe({
      next: result => {
        this.editModeSaving = false;
        this.editModeMessage = result.IsError
          ? result.ErrorMessage || 'Layout could not be saved.'
          : 'Dashboard layout saved.';
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Dashboard layout could not be saved.';
      }
    });
  }

  private saveWidget(request: DashboardWidgetSaveRequest, successMessage: string, allowCopyFallback = true): void {
    this.editModeSaving = true;
    const save$ = request.DashboardWidgetID && request.DashboardWidgetID > 0
      ? this.dashboardService.updateWidget(this.selectedDashboardId, request.DashboardWidgetID, request)
      : this.dashboardService.addWidget(this.selectedDashboardId, request);

    save$.subscribe({
      next: result => {
        if (result.IsError) {
          if (allowCopyFallback && !request.DashboardWidgetID && this.shouldCopyBeforeEdit(result.ErrorMessage)) {
            this.copyDashboardThenAddWidget(request, successMessage);
            return;
          }

          this.editModeSaving = false;
          this.editModeMessage = result.ErrorMessage || 'Widget could not be saved. Copy the dashboard before customising it.';
          return;
        }

        this.editModeSaving = false;
        this.editModeMessage = successMessage;
        this.addWidgetPanelVisible = false;
        this.loadDashboardData();
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Widget could not be saved.';
      }
    });
  }

  private copyDashboardThenAddWidget(request: DashboardWidgetSaveRequest, successMessage: string): void {
    const dashboardName = this.dashboards.find(d => d.DashboardID === this.selectedDashboardId)?.Name || 'Dashboard';
    const sourceWidgets = [...this.widgets];
    this.editModeMessage = 'Creating an editable dashboard copy before adding the widget.';

    this.dashboardService.copyDashboard({
      SourceDashboardID: this.selectedDashboardId,
      DashboardName: `${dashboardName} custom`
    }).subscribe({
      next: result => {
        if (result.IsError || result.DashboardID <= 0) {
          this.editModeSaving = false;
          this.editModeMessage = result.ErrorMessage || 'Dashboard could not be copied before adding the widget.';
          return;
        }

        this.selectedDashboardId = result.DashboardID;
        this.refreshDashboardListOnly();
        this.loadCopiedDashboardAndAddWidget(request, successMessage, sourceWidgets);
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Dashboard could not be copied before adding the widget.';
      }
    });
  }

  private loadCopiedDashboardAndAddWidget(
    request: DashboardWidgetSaveRequest,
    successMessage: string,
    sourceWidgets: DashboardWidgetResult[]
  ): void {
    this.dashboardService.getDashboardData({
      DashboardID: this.selectedDashboardId,
      Filters: this.requestFilters
    }).subscribe({
      next: response => {
        this.widgets = this.normaliseWidgetDisplay(response.Widgets);
        this.lastUpdatedDisplay = response.LastUpdatedDisplay;

        if (this.widgets.length === 0) {
          this.seedCopiedDashboardWidgetsThenAdd(request, successMessage, sourceWidgets);
          return;
        }

        this.saveWidget({
          ...request,
          DashboardID: this.selectedDashboardId,
          Seq: this.widgets.length + 1
        }, successMessage, false);
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Dashboard was copied, but the copied widgets could not be loaded.';
      }
    });
  }

  private seedCopiedDashboardWidgetsThenAdd(
    request: DashboardWidgetSaveRequest,
    successMessage: string,
    sourceWidgets: DashboardWidgetResult[]
  ): void {
    const addRequest: DashboardWidgetSaveRequest = {
      ...request,
      DashboardID: this.selectedDashboardId,
      Seq: sourceWidgets.length + 1
    };

    const saveRequests = [
      ...sourceWidgets.map((widget, index) =>
        this.dashboardService.addWidget(this.selectedDashboardId, this.toWidgetSaveRequest(widget, index + 1))
      ),
      this.dashboardService.addWidget(this.selectedDashboardId, addRequest)
    ];

    forkJoin(saveRequests).subscribe({
      next: results => {
        const failedResult = results.find(result => result.IsError);
        if (failedResult) {
          this.editModeSaving = false;
          this.editModeMessage = failedResult.ErrorMessage || 'Dashboard was copied, but widgets could not be saved.';
          return;
        }

        this.editModeSaving = false;
        this.editModeMessage = successMessage;
        this.addWidgetPanelVisible = false;
        this.loadDashboardData();
      },
      error: () => {
        this.editModeSaving = false;
        this.editModeMessage = 'Dashboard was copied, but widgets could not be saved.';
      }
    });
  }

  selectWidget(selection: DashboardWidgetSelection): void {
    this.drilldownTriggerElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    this.selectedWidget = selection.Widget;
    this.selectedDrilldownSelection = selection;
    this.drilldownPageNumber = 1;
    this.loadWidgetDrilldown();
    this.scheduleDrilldownScroll();
  }

  openDrilldownSelection(selection: DashboardSelectionContext): void {
    if (!this.selectedWidget) {
      return;
    }

    this.selectedDrilldownSelection = {
      Widget: this.selectedWidget,
      ...selection,
      ScopeType: selection.ScopeType || undefined
    };
    this.drilldownPageNumber = 1;
    this.loadWidgetDrilldown();
    this.scheduleDrilldownScroll();
  }

  loadWidgetDrilldown(): void {
    if (!this.selectedWidget) {
      return;
    }

    if (!this.selectedDrilldownSelection) {
      return;
    }

    if (this.isBluebirdDemo) {
      this.drilldown = buildBluebirdDemoDrilldown({
        DashboardID: this.selectedDashboardId,
        DashboardWidgetID: this.selectedWidget.DashboardWidgetID,
        MetricDefinitionKey: this.selectedWidget.MetricDefinitionKey,
        Filters: this.requestFilters,
        PageNumber: this.drilldownPageNumber,
        PageSize: this.drilldownPageSize,
        SortField: this.drilldownSortField,
        SortDirection: this.drilldownSortDirection,
        SelectionKey: this.selectedDrilldownSelection.SelectionKey,
        RowType: this.selectedDrilldownSelection.RowType
      });
      this.drilldownLoading = false;
      this.scheduleDrilldownScroll();
      return;
    }

    this.drilldownLoading = true;
    this.dashboardService.getWidgetDrilldown({
      DashboardID: this.selectedDashboardId,
      DashboardWidgetID: this.selectedWidget.DashboardWidgetID,
      MetricDefinitionKey: this.selectedWidget.MetricDefinitionKey,
      Filters: this.requestFilters,
      PageNumber: this.drilldownPageNumber,
      PageSize: this.drilldownPageSize,
      SortField: this.drilldownSortField,
      SortDirection: this.drilldownSortDirection,
      SelectionKey: this.selectedDrilldownSelection.SelectionKey,
      SegmentLabel: this.selectedDrilldownSelection.SegmentLabel,
      PeriodStart: this.selectedDrilldownSelection.PeriodStart,
      PeriodEnd: this.selectedDrilldownSelection.PeriodEnd,
      PeriodKey: this.selectedDrilldownSelection.PeriodKey,
      FrequencyID: this.selectedDrilldownSelection.FrequencyID,
      RequirementAuditID: this.selectedDrilldownSelection.AuditID,
      PillarID: this.selectedDrilldownSelection.PillarID,
      PillarKey: this.selectedDrilldownSelection.PillarKey,
      QuestionID: this.selectedDrilldownSelection.QuestionID,
      ScopeType: this.selectedDrilldownSelection.ScopeType,
      ScopeID: this.selectedDrilldownSelection.ScopeID,
      RowType: this.selectedDrilldownSelection.RowType
    }).pipe(
      tap(response => {
        this.drilldown = response;
        this.drilldownLoading = false;
        this.scheduleDrilldownScroll();
      })
    ).subscribe({
      error: () => {
        this.drilldownLoading = false;
      }
    });
  }

  private initialiseBluebirdDemo(): void {
    this.selectedDashboardId = BLUEBIRD_DEMO_DASHBOARD_ID;
    this.dashboards = [bluebirdDemoDashboard];
    this.showOfficeSelector = bluebirdDemoFilters.ShowOfficeSelector;
    this.officeSelectionRequired = false;
    this.locationFilterOptions = [...bluebirdDemoFilters.Offices];
    this.auditTypeFilterOptions = [{ ID: null, Label: this.customerContext.displayText('All Audit Types') }, ...bluebirdDemoFilters.AuditTypes];
    this.auditFilterOptions = [{ ID: null, Label: this.customerContext.displayText('All Audits') }, ...bluebirdDemoFilters.Audits];
    this.frequencyFilterOptions = [{ ID: null, Label: 'All Frequencies' }, ...bluebirdDemoFilters.Frequencies];
    this.qualityManagerFilterOptions = this.normalisePortfolioOptions(bluebirdDemoFilters.QualityManagers);
    this.businessDevelopmentManagerFilterOptions = this.normalisePortfolioOptions(bluebirdDemoFilters.BusinessDevelopmentManagers);
    this.showQualityManagerSelector = bluebirdDemoFilters.ShowQualityManagerSelector === true;
    this.showBusinessDevelopmentManagerSelector = bluebirdDemoFilters.ShowBusinessDevelopmentManagerSelector === true;
    this.filters = {
      StartDate: '2026-01-01',
      EndDate: '2026-08-10',
      LocationID: bluebirdDemoFilters.SelectedOfficeID ?? null,
      LocationName: bluebirdDemoFilters.Offices[0].Label,
      AuditTypeID: null,
      AuditType: this.customerContext.displayText('All Audit Types'),
      AuditID: null,
      AuditName: this.customerContext.displayText('All Audits'),
      FrequencyID: null,
      QualityManagerID: null,
      BusinessDevelopmentManagerID: null,
      IncludeSublocations: true
    };
    this.loadDashboardData();
  }

  private openBluebirdDemoActionDrilldown(): void {
    const widget = this.widgets.find(item => item.MetricDefinitionKey === 'bbc-overdue-action-trend');
    const metric = widget?.MetricValues?.find(item => item.Selection?.SelectionKey === 'overdue');
    if (!widget || !metric?.Selection) {
      return;
    }

    this.selectWidget({
      Widget: widget,
      SelectionKey: metric.Selection.SelectionKey,
      RowType: metric.Selection.RowType
    });
  }

  closeDrilldown(): void {
    const trigger = this.drilldownTriggerElement;
    this.selectedWidget = null;
    this.selectedDrilldownSelection = null;
    this.drilldown = null;
    this.drilldownLoading = false;
    this.drilldownTriggerElement = null;
    if (trigger?.isConnected) {
      window.setTimeout(() => trigger.focus({ preventScroll: true }), 0);
    }
  }

  changeDrilldownPage(pageNumber: number): void {
    this.drilldownPageNumber = pageNumber;
    this.loadWidgetDrilldown();
  }

  changeDrilldownSort(field: keyof DashboardWidgetDrilldownRow): void {
    if (this.drilldownSortField === field) {
      this.drilldownSortDirection = this.drilldownSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.drilldownSortField = field;
      this.drilldownSortDirection = 'asc';
    }
    this.drilldownPageNumber = 1;
    this.loadWidgetDrilldown();
  }

  private scheduleDrilldownScroll(): void {
    window.setTimeout(() => {
      this.drilldownRegion?.nativeElement.focus?.({ preventScroll: true });
      this.scrollDrilldownIntoView();
    }, 0);
  }

  private scrollDrilldownIntoView(): void {
    if (!this.drilldownRegion?.nativeElement) {
      return;
    }

    this.drilldownRegion.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  trackWidget(_: number, widget: DashboardWidgetResult): number {
    return widget.DashboardWidgetID;
  }

  get selectedDashboardCapabilities() {
    return this.dashboards.find(dashboard => dashboard.DashboardID === this.selectedDashboardId)?.Capabilities ?? {
      CanEditDashboard: false,
      CanCopyDashboard: false,
      CanDeleteDashboard: false,
      CanManageWidgets: false,
      CanSaveLayout: false
    };
  }

  get canEnterEditMode(): boolean {
    return false;
  }

  get isLocalBluebirdDashboard(): boolean {
    return this.selectedDashboardSummary?.Name === BLUEBIRD_DASHBOARD_NAME;
  }

  get isExecutiveBluebirdDashboard(): boolean {
    return this.selectedDashboardSummary?.Name === BLUEBIRD_EXECUTIVE_DASHBOARD_NAME;
  }

  get usesSimplifiedDashboardFilters(): boolean {
    return this.isLocalBluebirdDashboard || this.isExecutiveBluebirdDashboard;
  }

  private get requestFilters(): DashboardFilterState {
    return this.usingTemporaryPortfolioFilters
      ? {
          ...this.filters,
          QualityManagerID: null,
          BusinessDevelopmentManagerID: null
        }
      : this.filters;
  }

  get selectionPromptHeading(): string {
    if (this.locationFilterOptions.length === 0 && this.scopeFilterOptions.length === 0) {
      return 'No locations available';
    }
    return this.filtersPending ? 'Apply filters' : 'Select a location';
  }

  private markFiltersPending(): void {
    this.errorMessage = '';
    this.selectionPrompt = 'Select Apply filters to view the dashboard.';
    this.filtersPending = true;
    this.widgets = [];
    this.lastUpdatedDisplay = '';
    this.isLoading = false;
  }

  private get locationSelectionPrompt(): string {
    return this.isExecutiveBluebirdDashboard
      ? 'Please select a network or region to view the dashboard.'
      : 'Please select an office to view the dashboard.';
  }

  private get selectedDashboardSummary(): DashboardSummary | undefined {
    return this.dashboards.find(dashboard => dashboard.DashboardID === this.selectedDashboardId);
  }

  private shouldReturnHome(errorCode?: string): boolean {
    return errorCode === 'DASHACCESS001' || errorCode === 'DASH001';
  }

  private returnToHome(message = 'The dashboard is not available at the moment. You have been returned to the home page.'): void {
    if (this.returningHome) {
      return;
    }

    this.returningHome = true;
    this.isLoading = false;
    this.toastr.addToastr(new Toastr({ Type: 1, Message: message, Duration: 5000 }));
    this.router.navigateByUrl('/home');
  }
}
